import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@cieda/djayma-ui";
import {
  CATEGORY_ENUM,
  CATEGORY_LABEL_MAP,
  type CategorySlug,
  type Product,
} from "@/lib/validation/product";

interface BulkCategoryModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly products: readonly Product[];
  readonly onConfirm: (category: CategorySlug) => void;
  readonly isLoading: boolean;
}

export function BulkCategoryModal({
  open,
  onOpenChange,
  products,
  onConfirm,
  isLoading,
}: BulkCategoryModalProps) {
  const { t } = useTranslation("admin");
  const [category, setCategory] = useState<CategorySlug | "">("");

  useEffect(() => {
    if (!open) {
      setCategory("");
    }
  }, [open]);

  const preview = products.slice(0, 5);
  const remaining = products.length - preview.length;
  const isValid = category !== "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("products.bulk.category")} ({products.length} produit(s))
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="bulk-category-select">
              {t("products.bulk.category_select")}
            </Label>
            <Select
              value={category}
              onValueChange={(v) => setCategory(v as CategorySlug)}
            >
              <SelectTrigger id="bulk-category-select">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_ENUM.map((slug) => (
                  <SelectItem key={slug} value={slug}>
                    {CATEGORY_LABEL_MAP[slug] ?? slug}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <ul className="space-y-1 text-sm text-stone-700">
            {preview.map((p) => (
              <li key={p.id}>{p.name}</li>
            ))}
            {remaining > 0 && (
              <li className="text-stone-400">et {remaining} autre(s)</li>
            )}
          </ul>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            disabled={!isValid || isLoading}
            onClick={() => {
              if (category !== "") {
                onConfirm(category);
              }
            }}
          >
            Appliquer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
